import type {
  CheckReport,
  CreatorReferralView,
  CustomerReferralView,
  DeliveryView,
  DeveloperProfile,
  DeveloperView,
  DevListingView,
  EarningsView,
  ExpertPage,
  ExpertProfile,
  ExpertRequestView,
  ExpertSelfView,
  ExpertService,
  InstallResult,
  InstallView,
  ListingDetail,
  ListingAnalytics,
  ListingKind,
  ListingPage,
  MarketplaceStats,
  RemixResult,
  RemixView,
  ReviewPage,
  ReviewStatus,
  ReviewView as MarketplaceReviewView,
  UsageStatement,
  VersionView,
  WishView,
} from '@dunea/shared';
import httpClient from '~/lib/http-client';

// Marketplace REST client (frozen contract: packages/shared/src/marketplace/contracts.ts).
// Owner routes take the app id, which IS the chat id.

const seg = encodeURIComponent;
const M = '/api/marketplace';

export type ListingSort = 'popular' | 'rating' | 'new';

export interface ListingQuery {
  kind: ListingKind;
  category?: string;
  q?: string;
  sort?: ListingSort;
  page?: number;
}

function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }

  const text = search.toString();

  return text ? `?${text}` : '';
}

// ─── public catalog ───

export async function apiListListings(filter: ListingQuery): Promise<ListingPage> {
  const { data } = await httpClient.get<ListingPage>(
    `${M}/listings${query({
      kind: filter.kind,
      category: filter.category,
      q: filter.q?.trim(),
      sort: filter.sort,
      page: filter.page && filter.page > 1 ? filter.page : undefined,
    })}`,
  );

  return data;
}

export async function apiGetListing(slug: string): Promise<ListingDetail> {
  const { data } = await httpClient.get<ListingDetail>(`${M}/listings/${seg(slug)}`);
  return data;
}

export async function apiListListingReviews(slug: string, page = 1): Promise<ReviewPage> {
  const { data } = await httpClient.get<ReviewPage>(
    `${M}/listings/${seg(slug)}/reviews${query({ page: page > 1 ? page : undefined })}`,
  );

  return data;
}

export type ListingEventType = 'view' | 'install_click';

/** Analytics beacon. Fire and forget: never throws, never blocks the UI. */
export function sendListingEvent(slug: string, type: ListingEventType, source = 'workbench'): void {
  httpClient.post(`${M}/listings/${seg(slug)}/events`, { type, source }).catch(() => undefined);
}

export async function apiGetMarketplaceStats(): Promise<MarketplaceStats> {
  const { data } = await httpClient.get<MarketplaceStats>(`${M}/stats`);
  return data;
}

export async function apiGetDeveloperProfile(handle: string): Promise<DeveloperProfile> {
  const { data } = await httpClient.get<DeveloperProfile>(`${M}/developers/${seg(handle)}`);
  return data;
}

/**
 * Dunea's own themes, as theme manifests. The exact response shape is not in
 * the frozen contract; callers normalise it with `normalizeFirstPartyThemes`.
 */
export async function apiListFirstPartyThemes(): Promise<unknown> {
  const { data } = await httpClient.get<unknown>(`${M}/themes/first-party`);
  return data;
}

// ─── owner installs ───

const apps = (appId: string) => `${M}/apps/${seg(appId)}/installs`;

export async function apiListInstalls(appId: string): Promise<InstallView[]> {
  const { data } = await httpClient.get<InstallView[]>(apps(appId));
  return data;
}

export interface InstallRequest {
  slug: string;
  permissions: string[];
  settings?: Record<string, unknown>;
  returnUrl: string;
  /** Monthly cap on paid tool calls + usage, in credits (needed when the plugin has either). */
  spendCapCredits?: number;
  /** Where the install started, for analytics (the server may ignore it). */
  source?: InstallSource;
}

export type InstallSource = 'workbench' | 'storefront';

export async function apiInstall(appId: string, body: InstallRequest): Promise<InstallResult> {
  const { data } = await httpClient.post<InstallResult>(apps(appId), body);
  return data;
}

export async function apiUpdateInstall(
  appId: string,
  installId: string,
  patch: { settings?: Record<string, unknown>; permissions?: string[]; spendCapCredits?: number },
): Promise<InstallView> {
  const { data } = await httpClient.patch<InstallView>(`${apps(appId)}/${seg(installId)}`, patch);
  return data;
}

export async function apiUninstall(appId: string, installId: string): Promise<void> {
  await httpClient.delete(`${apps(appId)}/${seg(installId)}`);
}

export async function apiCreatePanelSession(appId: string, installId: string): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>(`${apps(appId)}/${seg(installId)}/panel-session`, {});
  return data;
}

export async function apiGetUsage(appId: string, month?: string): Promise<UsageStatement> {
  const { data } = await httpClient.get<UsageStatement>(`${M}/apps/${seg(appId)}/usage${query({ month })}`);
  return data;
}

export interface FunctionHealth {
  installId: string;
  slug: string;
  name: string;
  functions: { target: string; title: string }[];
  /** Times today (UTC) the plugin's checkout functions failed and Dunea used the fallback. */
  failuresToday: number;
}

/** The app's installed checkout functions and today's fallbacks. */
export async function apiGetFunctionHealth(appId: string): Promise<FunctionHealth[]> {
  const { data } = await httpClient.get<{ functions: FunctionHealth[] }>(`/api/plugins/functions/apps/${seg(appId)}`);
  return data.functions;
}

/** Start a new site from a template. Paid templates return a Stripe Checkout URL instead. */
export async function apiRemixListing(slug: string, body: { name?: string; returnUrl: string }): Promise<RemixResult> {
  const { data } = await httpClient.post<RemixResult>(`${M}/listings/${seg(slug)}/remix`, body);
  return data;
}

/** My template remixes, newest first (how a paid remix is found after Checkout). */
export async function apiListMyRemixes(): Promise<RemixView[]> {
  const { data } = await httpClient.get<RemixView[]>(`${M}/me/remixes`);
  return data;
}

// ─── experts ───

export interface ExpertQuery {
  country?: string;
  service?: string;
  q?: string;
  page?: number;
}

export async function apiListExperts(filter: ExpertQuery = {}): Promise<ExpertPage> {
  const { data } = await httpClient.get<ExpertPage>(
    `${M}/experts${query({
      country: filter.country,
      service: filter.service?.trim(),
      q: filter.q?.trim(),
      page: filter.page && filter.page > 1 ? filter.page : undefined,
    })}`,
  );

  return data;
}

export async function apiGetExpert(handle: string): Promise<ExpertProfile> {
  const { data } = await httpClient.get<ExpertProfile>(`${M}/experts/${seg(handle)}`);
  return data;
}

export interface ExpertRequestInput {
  message: string;
  service?: string;
  budgetCents?: number;
  appId?: string;
}

export async function apiRequestExpert(handle: string, body: ExpertRequestInput): Promise<ExpertRequestView> {
  const { data } = await httpClient.post<ExpertRequestView>(`${M}/experts/${seg(handle)}/requests`, body);
  return data;
}

export async function apiListMyExpertRequests(): Promise<ExpertRequestView[]> {
  const { data } = await httpClient.get<ExpertRequestView[]>(`${M}/me/expert-requests`);
  return data;
}

// ─── wishlist ───

export interface WishQuery {
  sort?: 'top' | 'new';
  kind?: ListingKind;
  status?: WishView['status'];
}

export async function apiListWishes(filter: WishQuery = {}): Promise<WishView[]> {
  const { data } = await httpClient.get<WishView[]>(`${M}/wishes${query({ ...filter })}`);
  return data;
}

export async function apiCreateWish(body: { kind: ListingKind; title: string; body?: string }): Promise<WishView> {
  const { data } = await httpClient.post<WishView>(`${M}/wishes`, body);
  return data;
}

/** Toggles my vote; returns the wish with the new count. */
export async function apiVoteWish(id: string): Promise<WishView> {
  const { data } = await httpClient.post<WishView>(`${M}/wishes/${seg(id)}/vote`, {});
  return data;
}

// ─── customer referrals ("invite a business") ───

export async function apiGetCustomerReferral(): Promise<CustomerReferralView> {
  const { data } = await httpClient.get<CustomerReferralView>('/api/referrals/me');
  return data;
}

export async function apiWriteReview(
  slug: string,
  body: { rating: number; title?: string; body?: string },
): Promise<MarketplaceReviewView> {
  const { data } = await httpClient.post<MarketplaceReviewView>(`${M}/listings/${seg(slug)}/reviews`, body);
  return data;
}

// ─── developer portal ───

const DEV = `${M}/dev`;

export async function apiGetDeveloperMe(): Promise<DeveloperView | null> {
  const { data } = await httpClient.get<DeveloperView | null>(`${DEV}/me`);
  return data ?? null;
}

export interface DeveloperProfileInput {
  handle: string;
  displayName: string;
  supportEmail: string;
  website?: string;
  bio?: string;
}

export async function apiCreateDeveloper(body: DeveloperProfileInput): Promise<DeveloperView> {
  const { data } = await httpClient.post<DeveloperView>(`${DEV}/me`, body);
  return data;
}

export async function apiCreateDevToken(label: string): Promise<{ token: string; prefix: string }> {
  const { data } = await httpClient.post<{ token: string; prefix: string }>(`${DEV}/tokens`, { label });
  return data;
}

export async function apiRevokeDevToken(id: string): Promise<void> {
  await httpClient.delete(`${DEV}/tokens/${seg(id)}`);
}

export async function apiListDevListings(): Promise<DevListingView[]> {
  const { data } = await httpClient.get<DevListingView[]>(`${DEV}/listings`);
  return data;
}

export async function apiCreateDevListing(
  manifest: unknown,
): Promise<{ listing: DevListingView; clientSecret: string }> {
  const { data } = await httpClient.post<{ listing: DevListingView; clientSecret: string }>(`${DEV}/listings`, {
    manifest,
  });

  return data;
}

export async function apiCreateVersion(slug: string, manifest: unknown, changelog?: string): Promise<VersionView> {
  const { data } = await httpClient.post<VersionView>(`${DEV}/listings/${seg(slug)}/versions`, {
    manifest,
    ...(changelog?.trim() ? { changelog: changelog.trim() } : {}),
  });

  return data;
}

export async function apiSubmitVersion(slug: string, version: string): Promise<VersionView> {
  const { data } = await httpClient.post<VersionView>(
    `${DEV}/listings/${seg(slug)}/versions/${seg(version)}/submit`,
    {},
  );

  return data;
}

export async function apiRotateSecret(slug: string): Promise<{ clientSecret: string }> {
  const { data } = await httpClient.post<{ clientSecret: string }>(`${DEV}/listings/${seg(slug)}/rotate-secret`, {});
  return data;
}

export async function apiListDeliveries(slug: string, status?: DeliveryView['status']): Promise<DeliveryView[]> {
  const { data } = await httpClient.get<DeliveryView[]>(`${DEV}/listings/${seg(slug)}/deliveries${query({ status })}`);
  return data;
}

export async function apiSendTestWebhook(slug: string): Promise<DeliveryView> {
  const { data } = await httpClient.post<DeliveryView>(`${DEV}/listings/${seg(slug)}/test-webhook`, {});
  return data;
}

export async function apiGetEarnings(range: { from?: string; to?: string } = {}): Promise<EarningsView> {
  const { data } = await httpClient.get<EarningsView>(`${DEV}/earnings${query(range)}`);
  return data;
}

/** Run the submission checks on a manifest without saving anything. */
export async function apiCheckManifest(manifest: unknown): Promise<CheckReport> {
  const { data } = await httpClient.post<CheckReport>(`${DEV}/check`, { manifest });
  return data;
}

/** Install the newest version on one of your apps (dev channel, never billed). */
export async function apiDevInstall(slug: string, appId: string): Promise<InstallView> {
  const { data } = await httpClient.post<InstallView>(`${DEV}/listings/${seg(slug)}/dev-install`, { appId });
  return data;
}

/** Make an older approved version the current one (rollback). */
export async function apiReleaseVersion(slug: string, version: string): Promise<VersionView> {
  const { data } = await httpClient.post<VersionView>(
    `${DEV}/listings/${seg(slug)}/versions/${seg(version)}/release`,
    {},
  );

  return data;
}

export async function apiGetListingAnalytics(slug: string, days = 30): Promise<ListingAnalytics> {
  const { data } = await httpClient.get<ListingAnalytics>(`${DEV}/listings/${seg(slug)}/analytics${query({ days })}`);
  return data;
}

export async function apiGetReviewStatus(slug: string): Promise<ReviewStatus> {
  const { data } = await httpClient.get<ReviewStatus>(`${DEV}/listings/${seg(slug)}/review-status`);
  return data;
}

export async function apiGetCreatorReferral(): Promise<CreatorReferralView> {
  const { data } = await httpClient.get<CreatorReferralView>(`${DEV}/referral`);
  return data;
}

export async function apiGetExpertSelf(): Promise<ExpertSelfView | null> {
  const { data } = await httpClient.get<ExpertSelfView | null>(`${DEV}/expert`);
  return data ?? null;
}

export interface ExpertProfileInput {
  headline: string;
  about: string;
  services: ExpertService[];
  countries: string[];
  languages: string[];
  portfolio: string[];
  acceptingWork: boolean;
}

export async function apiSaveExpertSelf(body: ExpertProfileInput): Promise<ExpertSelfView> {
  const { data } = await httpClient.put<ExpertSelfView>(`${DEV}/expert`, body);
  return data;
}

export async function apiListExpertInbox(status?: ExpertRequestView['status']): Promise<ExpertRequestView[]> {
  const { data } = await httpClient.get<ExpertRequestView[]>(`${DEV}/expert/requests${query({ status })}`);
  return data;
}

export type ExpertRequestAction = 'accept' | 'decline' | 'complete';

export async function apiActOnExpertRequest(id: string, action: ExpertRequestAction): Promise<ExpertRequestView> {
  const { data } = await httpClient.post<ExpertRequestView>(`${DEV}/expert/requests/${seg(id)}/${action}`, {});
  return data;
}

/** Hand a site you built to a client: they claim it from the returned URL. */
export async function apiCreateHandoff(chatId: string): Promise<{ claimUrl: string }> {
  const { data } = await httpClient.post<{ claimUrl: string }>(`${DEV}/expert/handoffs`, { chatId });
  return data;
}

export async function apiClaimWish(id: string, listingSlug?: string): Promise<WishView> {
  const { data } = await httpClient.post<WishView>(
    `${DEV}/wishes/${seg(id)}/claim`,
    listingSlug ? { listingSlug } : {},
  );
  return data;
}

export async function apiConnectPayouts(returnUrl: string): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>(`${DEV}/payouts/connect`, { returnUrl });
  return data;
}

export async function apiReplyToMarketplaceReview(reviewId: string, reply: string): Promise<void> {
  await httpClient.post(`${DEV}/reviews/${seg(reviewId)}/reply`, { reply });
}

// ─── admin ───

const ADMIN = `${M}/admin`;

export async function apiGetReviewQueue(): Promise<VersionView[]> {
  const { data } = await httpClient.get<VersionView[]>(`${ADMIN}/queue`);
  return data;
}

export async function apiApproveVersion(id: string, notes?: string): Promise<void> {
  await httpClient.post(`${ADMIN}/versions/${seg(id)}/approve`, notes?.trim() ? { notes: notes.trim() } : {});
}

export async function apiRejectVersion(id: string, notes: string, codes: string[]): Promise<void> {
  await httpClient.post(`${ADMIN}/versions/${seg(id)}/reject`, { notes: notes.trim(), codes });
}

export async function apiListPendingExperts(): Promise<ExpertSelfView[]> {
  const { data } = await httpClient.get<ExpertSelfView[]>(`${ADMIN}/experts${query({ status: 'pending' })}`);
  return data;
}

export async function apiReviewExpert(id: string, decision: 'approve' | 'reject', notes?: string): Promise<void> {
  await httpClient.post(`${ADMIN}/experts/${seg(id)}/${decision}`, notes?.trim() ? { notes: notes.trim() } : {});
}

export async function apiSuspendListing(slug: string, reason: string): Promise<void> {
  await httpClient.post(`${ADMIN}/listings/${seg(slug)}/suspend`, { reason: reason.trim() });
}

export async function apiUnsuspendListing(slug: string): Promise<void> {
  await httpClient.post(`${ADMIN}/listings/${seg(slug)}/unsuspend`, {});
}

export async function apiFeatureListing(slug: string, featured: boolean): Promise<void> {
  await httpClient.post(`${ADMIN}/listings/${seg(slug)}/feature`, { featured });
}

export async function apiVerifyDeveloper(handle: string, verified: boolean): Promise<void> {
  await httpClient.post(`${ADMIN}/developers/${seg(handle)}/verify`, { verified });
}

// ─── admin refunds (marketplace purchases; admin-only until a refund policy exists) ───

export interface AdminListingCharge {
  /** Stripe charge (one-time sale) or invoice (subscription) id. */
  stripeRef: string;
  stream: 'sale' | 'subscription';
  gross: number;
  /** Cents refunded so far, per the earnings ledger. */
  refundedGross: number;
  currency: string;
  purchaseId: string | null;
  installId: string | null;
  buyerUserId: string | null;
  occurredAt: string;
}

export interface AdminRefundResult {
  refundId: string;
  status: string | null;
  amount: number;
  refundedTotal: number;
  full: boolean;
}

export async function apiAdminListingCharges(slug: string): Promise<AdminListingCharge[]> {
  const { data } = await httpClient.get<AdminListingCharge[]>(
    `${ADMIN}/billing/charges${query({ listing: slug.trim() })}`,
  );
  return data;
}

/** Refund through Stripe (reverses the developer's transfer and Dunea's fee pro rata). Omit amount for the rest. */
export async function apiAdminRefund(input: {
  stripeRef: string;
  amount?: number;
  note?: string;
}): Promise<AdminRefundResult> {
  const { data } = await httpClient.post<AdminRefundResult>(`${ADMIN}/billing/refunds`, input);
  return data;
}
